import React, { useEffect, useState } from "react";
import { ContentMainLayout } from "../../layout/contentLayout";
import { Box, Button, Stack, Typography } from "@mui/material";
import useResponsive from "../../hooks/useResponsive";

import BadgeImage from "../ui-elements/badge-image";
import ImageGrid from "../image-grid";
import { PostContentDetails } from "../../interface/posts";
import TypographyContent from "../ui-elements/typography-content";
import {
  formatDateAndTimeFromString,
  formatTextType,
} from "../../utils/date-setup";
import { CommentData, CommentReplyPropsData } from "../../interface/comment";
import { organizeCommentsByPost } from "../../utils/comment";
import TurnedInIcon from "@mui/icons-material/TurnedIn";
import TurnedInNotIcon from "@mui/icons-material/TurnedInNot";

import CommentUI from "../ui-elements/comment-ui";
import { apiBookmarkPost } from "../../api/post";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import { isInCookie, toggleCookieItem } from "../../utils/post-activity";
import UnAuthSignPetitionContainer from "./sign-petition";
import { useDrawer } from "../../hooks/useDrawer";
import { HandsFistIcon } from "../ui-elements/svg-icons";

const UnAuthenticatedPetition = ({
  post_details,
  comment_data,
}: {
  post_details: PostContentDetails;
  comment_data: CommentData[];
}) => {
  const isDesktop = useResponsive("down", "lg", "xl");
  const isMedium = useResponsive("down", "md", "xl");
  const isSmall = useResponsive("down", "sm", "xl");
  const account_profile_data = useSelector(
    (state: RootState) => state.content.account_profile_data
  );
  const { openDrawer } = useDrawer();
  const [comment_list, setComment_list] = useState<CommentReplyPropsData[]>([]);
  const [reply_state, setReply_state] = useState({
    reply_modal: false,
    reply_data: {},
  });

  const handle_open_reply = (reply_data: CommentReplyPropsData) => {};

  useEffect(() => {
    const fetchComment = async () => {
      // filter comment based on post
      const organizedComments = organizeCommentsByPost(
        comment_data,
        post_details?.id
      );
      setComment_list(organizedComments);
    };
    fetchComment();
  }, []);

  return (
    <ContentMainLayout>
      {isDesktop && (
        <Box sx={{ position: "fixed", right: 10, width: 80, height: "3rem" }}>
          <Button
            variant="contained"
            onClick={() =>
              openDrawer(<UnAuthSignPetitionContainer data={post_details} />)
            }
          >
            <HandsFistIcon />
          </Button>
        </Box>
      )}
      <Stack
        direction={"row"}
        spacing={4}
        justifyContent={isDesktop ? "center" : "space-between"}
      >
        <Stack width={isSmall ? "100%" : 560}>
          <Box
            id="main-scroll-container"
            sx={{
              width: isMedium ? "100%" : "95%",
              mx: isMedium ? 0 : 2,
              mt: "auto",
              height: "76vh",
              overflowY: "auto",
              pt: "85px",
              px: isMedium ? 0 : 3,
              position: "relative",
            }}
          >
            <PetitionContentDetails
              post_details={post_details}
              handle_open_reply={handle_open_reply}
              comment_list={comment_list}
            />
            <Box
              sx={{
                position: "fixed",
                width: isMedium ? "80%" : 600,
                mx: "auto",
                bottom: 10,
                p: 1,
                py: 2,
                backgroundColor: "#FAFAFA",
              }}
            ></Box>
          </Box>
        </Stack>
        <Stack
          width={isDesktop ? "0" : "35%"}
          sx={{
            display: isDesktop ? "none" : "block",
          }}
          height={505}
        >
          <Box
            sx={{
              width: "100%",
              pt: "85px",
              height: "76vh",
            }}
          >
            <UnAuthSignPetitionContainer data={post_details} />
          </Box>
        </Stack>
      </Stack>
    </ContentMainLayout>
  );
};

export default UnAuthenticatedPetition;

const PetitionContentDetails = ({
  post_details,
  comment_list,
  handle_open_reply,
}: {
  post_details: PostContentDetails;
  handle_open_reply: (e: any) => void;
  comment_list: CommentReplyPropsData[];
}) => {
  const [bookmarked, setBookmarked] = useState(false);

  // Initialize states based on cookies
  useEffect(() => {
    setBookmarked(isInCookie("bookmarks", post_details?.id));
  }, [post_details?.id]);

  const handle_bookmark = () => {
    const newBookmarkedState = toggleCookieItem("bookmarks", post_details?.id);
    setBookmarked(newBookmarkedState);
  };

  // handle bookmark
  const bookmark_post = async (post_id: number) => {
    handle_bookmark();
    try {
      const response = await apiBookmarkPost(post_id);
      if (response.status === 200) {
        //
      } else {
      }
    } catch (error) {}
  };

  const handleBookmark = (id: number) => {
    bookmark_post(id);
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          mb: 3,
        }}
      >
        <Stack direction={"row"} alignItems={"center"} spacing={2}>
          <img
            src={"/assets/images/newsfeed/profile-01.png"}
            style={{ width: 50, height: 50, borderRadius: 40 }}
          />
          <Stack>
            <Stack direction={"row"} spacing={1}>
              <Typography variant="subtitle2" color="black">
                {post_details?.author}
              </Typography>
              <BadgeImage badge={1} />
            </Stack>

            <Typography
              variant="caption"
              sx={{
                color: "#4A5E6D",
                fontWeight: 400,
              }}
            >
              {formatDateAndTimeFromString(post_details?.created_at)}
            </Typography>
            <Typography
              variant="caption"
              color="primary"
              sx={{
                fontWeight: 400,
                fontStyle: "italic",
              }}
            >
              {formatTextType(post_details?.post_type)}
            </Typography>
          </Stack>
        </Stack>
      </Box>
      <Box sx={{ ml: 4 }}>
        <Stack>
          <Typography
            color="black"
            sx={{
              fontSize: 18,
              marginBottom: 2,
              fontWeight: 500,
            }}
          >
            {post_details?.title}
          </Typography>
          <ImageGrid
            images={post_details?.media} // Pass images as an array
          />
        </Stack>
        <Box sx={{ mt: "10px", display: "block" }}>
          <TypographyContent content={post_details?.context} />
        </Box>
      </Box>
      {comment_list?.length > 0 &&
        comment_list?.map((comment, index) => (
          <></>
          // <CommentUI
          //   key={index}
          //   id={comment?.id}
          //   author_img={comment?.author_photo_url}
          //   author_name={comment?.author_name}
          //   date={comment?.commented_at}
          //   comment={comment?.comment}
          //   likes={comment?.likes}
          //   sx={{}}
          //   handle_open_reply={handle_open_reply}
          //   replies={comment?.replies} // Optional since replies may be undefined
          // />
        ))}
    </>
  );
};
